/**
 * PharmacovigilanceRegister — PV-1. The real Spectropharma pharmacovigilance
 * operation: the registered-product portfolio (product master) + the PV tracker
 * registers (ICSR / PBRER / Signal / RMP / Safety-Variation / Additional-RMM /
 * Literature). Route: /pharmacovigilance. Bilingual (en-US / ar-SA).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@edms/design-system';
import styles from '../Recalls/Recalls.module.css';
import { apiFetch } from '../../api/client';

interface PvProduct {
  product_no: number | null; trade_name: string; active_ingredients: string | null;
  concentration: string | null; dosage_form: string | null; marketing_status: string | null;
  registration_number: string | null; certificate_issue_date: string | null;
  required_pv_document: string | null; row_data: Record<string, unknown>;
}
interface PvRegRow {
  tracker: string; product: string | null; api: string | null; title: string | null;
  status: string | null; ref_date: string | null; row_no: number; row_data: Record<string, unknown>;
}
interface PvResp {
  products: PvProduct[]; products_total: number;
  datasets: Record<string, PvRegRow[]>;
  counts: Record<string, number>;
}

const TABS = ['PRODUCTS', 'RMP', 'ADDITIONAL_RMM', 'ICSR', 'PBRER', 'SIGNAL', 'SAFETY_VARIATION', 'LITERATURE'] as const;

const fmtDate = (d: string | null) => (d ? String(d).slice(0, 10) : '—');

export function PharmacovigilanceRegister() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<(typeof TABS)[number]>('PRODUCTS');
  const q = useQuery<PvResp>({
    queryKey: ['pharmacovigilance', 'registers'],
    queryFn: ({ signal }) => apiFetch<PvResp>('/api/v1/pharmacovigilance/registers', { signal }),
    staleTime: 60_000,
  });
  const tp = (k: string, opts?: Record<string, unknown>) => t(`pv.${k}`, opts ?? {});
  const tabLabel = (k: string) => t(`pv.tab.${k}`, { defaultValue: k });
  const count = (k: string) => (k === 'PRODUCTS' ? q.data?.products_total ?? 0 : q.data?.counts?.[k] ?? 0);

  return (
    <main className={styles.page}>
      <PageHero eyebrow={tp('eyebrow')} title={tp('title')} subtitle={tp('subtitle')} />

      <div className={styles.filters} role="tablist" aria-label={tp('title')}>
        {TABS.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
            className={`${styles.btn} ${tab === k ? styles.btnPrimary : styles.btnNeutral}`}
            onClick={() => setTab(k)}>
            {tabLabel(k)} <span className={styles.pill}>{count(k)}</span>
          </button>
        ))}
      </div>

      {q.isLoading ? <div className={styles.empty}>{tp('loading')}</div>
        : q.isError || !q.data ? <div className={styles.empty}>{tp('loadError')}</div>
          : tab === 'PRODUCTS' ? (
            <table className={styles.table}>
              <thead><tr>
                <th>{tp('thNo')}</th><th>{tp('thProduct')}</th><th>{tp('thApi')}</th>
                <th>{tp('thConcentration')}</th><th>{tp('thDosageForm')}</th>
                <th>{tp('thMarketingStatus')}</th><th>{tp('thRegNo')}</th><th>{tp('thCertDate')}</th>
              </tr></thead>
              <tbody>
                {q.data.products.map((p, i) => (
                  <tr key={i}>
                    <td>{p.product_no ?? '—'}</td>
                    <td>{p.trade_name}</td>
                    <td>{p.active_ingredients ?? '—'}</td>
                    <td>{p.concentration ?? '—'}</td>
                    <td>{p.dosage_form ?? '—'}</td>
                    <td><span className={`${styles.pill} ${styles.cls3}`}>{p.marketing_status ?? '—'}</span></td>
                    <td className={styles.mono}>{p.registration_number ?? '—'}</td>
                    <td>{fmtDate(p.certificate_issue_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (q.data.datasets[tab] ?? []).length === 0 ? (
            <div className={styles.empty}>{tp('emptyRegister')}</div>
          ) : (
            <table className={styles.table}>
              <thead><tr>
                <th>{tp('thProduct')}</th><th>{tp('thApi')}</th><th>{tp('thTitle')}</th>
                <th>{tp('thStatus')}</th><th>{tp('thRefDate')}</th>
              </tr></thead>
              <tbody>
                {(q.data.datasets[tab] ?? []).map((r, i) => (
                  <tr key={i}>
                    <td>{r.product ?? '—'}</td>
                    <td>{r.api ?? '—'}</td>
                    <td>{r.title ?? '—'}</td>
                    <td><span className={`${styles.pill} ${styles.cls3}`}>{r.status ?? '—'}</span></td>
                    <td>{fmtDate(r.ref_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </main>
  );
}
